import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Newspaper, Eye, EyeOff } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, CategorySelect, UploadPicker } from "./shared";
import RichText from "./RichText";

const EMPTY = { title: "", content: "", featured_image: "", category: "", tags: [], status: "draft", slug: "", author: "Anansha Welfare Society", seo_title: "", seo_description: "", image_alt: "" };

const toForm = (b) => ({
  title: b.title, content: b.content, featured_image: b.featured_image || "", category: b.category || "",
  tags: b.tags || [], status: b.status, slug: b.slug || "", author: b.author || "Anansha Welfare Society",
  seo_title: b.seo_title || "", seo_description: b.seo_description || "", image_alt: b.image_alt || "",
});

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [tagsText, setTagsText] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/blogs").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setTagsText(""); setEditing(null); setShowForm(true); };
  const openEdit = (b) => {
    setForm(toForm(b));
    setTagsText((b.tags || []).join(", "));
    setEditing(b.id);
    setShowForm(true);
  };

  const save = async (status) => {
    if (form.title.trim().length < 2) {
      toast.error("शीर्षक आवश्यक है");
      return;
    }
    const payload = { ...form, status, tags: tagsText.split(",").map((x) => x.trim()).filter(Boolean) };
    try {
      if (editing) {
        await api.put(`/admin/blogs/${editing}`, payload);
        toast.success("ब्लॉग अपडेट हुआ");
      } else {
        await api.post("/admin/blogs", payload);
        toast.success(status === "published" ? "ब्लॉग प्रकाशित हुआ" : "ड्राफ्ट सहेजा गया");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleStatus = async (b) => {
    try {
      await api.put(`/admin/blogs/${b.id}`, { ...toForm(b), status: b.status === "published" ? "draft" : "published" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह ब्लॉग हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/blogs/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (showForm) {
    return (
      <div className="bg-white rounded-2xl border border-ngo-deep/10 p-6 sm:p-8" data-testid="blog-editor">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-xl text-ngo-ink flex items-center gap-2">
            <Newspaper size={20} className="text-ngo-deep" /> {editing ? "ब्लॉग संपादित करें" : "नया ब्लॉग"}
          </h3>
          <button data-testid="blog-cancel" onClick={() => setShowForm(false)} className="text-sm text-ngo-deep underline">वापस जाएं</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">ब्लॉग शीर्षक *</label>
            <input data-testid="blog-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी</label>
            <CategorySelect testid="blog-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} allowEmpty />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">टैग (कॉमा से अलग करें)</label>
            <input data-testid="blog-tags" className={inputCls} value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="शिक्षा, वाराणसी" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">मुख्य फोटो (Featured Image)</label>
            <div className="flex gap-2 items-center">
              <input data-testid="blog-image" className={inputCls} value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} placeholder="फोटो अपलोड करें या URL डालें" />
              <UploadPicker testid="blog-image-upload" context="blog" label="अपलोड" onUploaded={(url) => setForm((p) => ({ ...p, featured_image: url }))} />
            </div>
            {form.featured_image && <img src={resolveMedia(form.featured_image)} alt="मुख्य फोटो" className="mt-3 h-32 rounded-xl object-cover border border-ngo-deep/10" />}
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">ब्लॉग सामग्री</label>
            <RichText testid="blog-content" value={form.content} onChange={(html) => setForm((p) => ({ ...p, content: html }))} />
          </div>
          <div className="sm:col-span-2 bg-ngo-mint/60 rounded-2xl p-5 grid sm:grid-cols-2 gap-4">
            <p className="sm:col-span-2 font-heading font-bold text-ngo-forest text-sm">SEO सेटिंग्स (Google के लिए)</p>
            <div>
              <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">URL स्लग (English अक्षरों में)</label>
              <input data-testid="blog-slug" className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="health-camp-varanasi" />
            </div>
            <div>
              <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">लेखक</label>
              <input data-testid="blog-author" className={inputCls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">SEO शीर्षक</label>
              <input data-testid="blog-seo-title" className={inputCls} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="खाली छोड़ें तो ब्लॉग शीर्षक इस्तेमाल होगा" />
            </div>
            <div>
              <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">फोटो ALT टेक्स्ट</label>
              <input data-testid="blog-image-alt" className={inputCls} value={form.image_alt} onChange={(e) => setForm({ ...form, image_alt: e.target.value })} placeholder="Anansha Welfare Society health camp in Varanasi" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">Meta विवरण (150-160 अक्षर)</label>
              <textarea data-testid="blog-seo-description" rows={2} maxLength={300} className={inputCls} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="Google सर्च में दिखने वाला छोटा विवरण" />
            </div>
          </div>
        </div>
        <div className="mt-7 flex flex-wrap gap-3">
          <button data-testid="blog-publish" onClick={() => save("published")} className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
            <Eye size={16} /> प्रकाशित करें
          </button>
          <button data-testid="blog-draft" onClick={() => save("draft")} className="inline-flex items-center gap-2 px-7 py-3 rounded-full border-2 border-ngo-deep text-ngo-deep font-bold hover:bg-ngo-mint transition-colors duration-300">
            ड्राफ्ट सहेजें
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button data-testid="blog-new" onClick={openNew} className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
        <Plus size={16} /> नया ब्लॉग लिखें
      </button>
      {items.length === 0 && <p data-testid="blog-empty" className="text-center text-ngo-ink/50 py-14">अभी कोई ब्लॉग नहीं है। पहला ब्लॉग लिखें!</p>}
      <div data-testid="blog-list" className="space-y-4">
        {items.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-5 flex flex-wrap items-center gap-4">
            {b.featured_image ? (
              <img src={resolveMedia(b.featured_image)} alt={b.title} className="w-20 h-14 object-cover rounded-lg" />
            ) : (
              <div className="w-20 h-14 rounded-lg bg-ngo-mint flex items-center justify-center text-ngo-deep"><Newspaper size={20} /></div>
            )}
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-ngo-ink">{b.title}</p>
              <p className="text-xs text-ngo-ink/50 mt-1">
                {b.status === "published" ? (
                  <span className="text-ngo-deep font-bold">● प्रकाशित</span>
                ) : (
                  <span className="text-ngo-gold font-bold">● ड्राफ्ट</span>
                )}
                {b.published_at && ` • ${new Date(b.published_at).toLocaleDateString("hi-IN")}`}
                {b.category && ` • ${b.category}`}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`blog-toggle-${b.id}`} onClick={() => toggleStatus(b)} aria-label="प्रकाशन स्थिति बदलें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200">
                {b.status === "published" ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <button data-testid={`blog-edit-${b.id}`} onClick={() => openEdit(b)} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200">
                <Pencil size={15} />
              </button>
              <button data-testid={`blog-delete-${b.id}`} onClick={() => remove(b.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
